<template>
    <div class="retr__row-header retr__row-second">
        <div class="retr-current__content-wrapper">
            <!-- Condition name -->
            <div class="retr-current__condition-name">
                {{ condition.condition_name }}
                <a-icon
                    v-if="condition.tooltip"
                    v-tooltip="condition.tooltip"
                    class="retr-current__condition-icon"
                    icon="Info"
                    width="16px"
                />
            </div>

            <!-- Retro value with percentage -->
            <div
                v-if="hasRetroValue"
                class="retr__value retr-current__retro-value"
                :class="{ 'font-weight-bold': condition.is_condition_realized }"
            >
                <span v-if="showValuePercentage">
                    ({{ formattedPercentage }}%)
                </span>
                <span>
                    {{ condition.retro | currency }}
                </span>
            </div>

            <!-- Index list condition counter -->
            <div
                v-else-if="isIndexListConditionActive"
                class="retr-current__index-list-condition"
            >
                <span>
                    {{ realizedIndexCount }} / {{ minimumSku }}
                </span>
                <a-icon
                    class="retr-current__status-icon"
                    width="16px"
                    height="16px"
                    :icon="isIndexListAllConditionsRealized ? 'Ok' : 'ErrorCircle'"
                />
            </div>

            <!-- Sales goal threshold status indicator -->
            <a-icon
                v-if="hasSalesGoal"
                class="retr-current__status-icon"
                :icon="isSalesGoalConditionRealized ? 'Ok' : 'ErrorCircle'"
                :height="isSalesGoalConditionRealized ? '16px' : '18px'"
                :width="isSalesGoalConditionRealized ? '16px' : '18px'"
            />
        </div>

        <!-- Details button -->
        <div class="retr__options">
            <the-btn
                v-if="hasIndexList"
                text="Więcej"
                class="btn-sm btn-outline-custom w-100 retr-current__details-btn"
                :disabled="isIndexListAllConditionsRealized"
                @click="$store.dispatch('openModal', {
                    componentPath: 'my-business/retr/MissingIndexModal',
                    args:{
                        indexList: condition.index_list.filter(index => index.is_condition_realized === false),
                        manufacturerName,
                        conditionType,
                        missingIndexCount
                    }
                })"
            />

        </div>
    </div>
</template>

<script>
export default {
    name: 'RetrCurrentConditionItem',
    props: {
        condition: {
            type: Object,
            required: true
        },
        conditionType: {
            type: String,
            required: false
        },
        minimumSku: {
            type: Number,
            required: false
        },
        showValuePercentage: {
            type: Boolean,
            required: false,
            default: true
        },
        manufacturerName: {
            type: String,
            default: ''
        },
        isSalesGoalConditionRealized: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    computed: {
        /**
         * Check if condition has retro value with percentage
         */
        hasRetroValue() {
            return this.condition.retro !== null && this.condition.percent_conditions !== null
        },

        /**
         * Check if condition has index list
         */
        hasIndexList() {
            return this.condition.index_list && this.condition.index_list.length > 0
        },

        /**
         * Check if index list condition should be displayed
         */
        isIndexListConditionActive() {
            return this.condition.retro === null &&
                this.condition.percent_conditions === null &&
                this.hasIndexList
        },

        /**
         * Check if minimum required index list conditions are realized
         */
        isIndexListAllConditionsRealized() {
            return this.hasIndexList &&
                this.minimumSku &&
                this.realizedIndexCount >= this.minimumSku
        },

        /**
         * Count of realized index conditions
         */
        realizedIndexCount() {
            return this.hasIndexList
                ? this.condition.index_list.filter(index => index.is_condition_realized).length
                : 0
        },

        /**
         * Total count of index conditions
         */
        totalIndexCount() {
            return this.hasIndexList ? this.condition.index_list.length : 0
        },

        /**
         * Count of missing index conditions
         */
        missingIndexCount() {
            return this.minimumSku - this.realizedIndexCount
        },

        /**
         * Check if condition has sales goal
         */
        hasSalesGoal() {
            return !!this.condition.sales_goal
        },

        /**
         * Format percentage for display (with 1 decimal place)
         */
        formattedPercentage() {
            return (this.condition.percent_conditions * 100).toLocaleString('pl-PL', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })
        }
    }
}
</script>

<style lang="scss">
@import '../../../styles/components/retr-common';

.retr-current {
    &__content-wrapper {
        display: flex;
        justify-content: space-between;
        flex-grow: 1;
        align-items: center;
        gap: 1rem;
    }

    &__condition-name {
        flex-grow: 1;
        margin: 0;
    }

    &__condition-icon {
        display: inline-block;
        margin-left: 0.5rem;
        margin-top: -2px;
        min-width: 16px;
        vertical-align: top;
    }

    &__retro-value {
        white-space: nowrap;
    }

    &__index-list-condition {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 0.5rem;
        white-space: nowrap;
    }

    &__status-icon {
        min-width: 16px;
    }

    &__details-btn {
        padding-left: 14px;
    }
}
</style>
